<template>
    <v-form ref="form" @submit.prevent="save">
        <v-card>
            <card-header :title="cardTitle" />
            <v-card-text>
                <v-row>
                    <v-col cols="12" md="8">
                        <v-text-field
                            v-model="model.title"
                            :label="
                                $t('resources.notification.attributes.title')
                            "
                            :counter="255"
                            :error-messages="titleErrors"
                            @input="$v.model.title.$touch()"
                            @blur="$v.model.title.$touch()"
                        />
                    </v-col>

                    <v-col cols="12" md="4">
                        <v-select
                            v-model="model.type"
                            :label="
                                $t('resources.notification.attributes.type')
                            "
                            :items="typeOptions"
                            item-text="text"
                            item-value="value"
                            :error-messages="typeErrors"
                            @input="$v.model.type.$touch()"
                            @blur="$v.model.type.$touch()"
                        />
                    </v-col>

                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model.number="model.days"
                            type="number"
                            v-bind="$t('int-config')"
                            :label="
                                $t('resources.notification.attributes.days')
                            "
                            :error-messages="daysErrors"
                            @input="$v.model.days.$touch()"
                            @blur="$v.model.days.$touch()"
                        />
                    </v-col>

                    <v-col cols="12" md="4">
                        <v-radio-group
                            v-model="model.when"
                            :label="
                                $t('resources.notification.attributes.when')
                            "
                            row
                        >
                            <v-radio
                                v-for="option in whenOptions"
                                :key="option.value"
                                :label="option.text"
                                :value="option.value"
                            />
                        </v-radio-group>
                    </v-col>

                    <v-col cols="12" md="4">
                        <v-select
                            v-model="model.action"
                            :label="
                                $t('resources.notification.attributes.action')
                            "
                            :items="actionOptions"
                            item-text="text"
                            item-value="value"
                            :error-messages="actionErrors"
                            @input="$v.model.action.$touch()"
                            @blur="$v.model.action.$touch()"
                        />
                    </v-col>

                    <v-col cols="12">
                        <v-radio-group
                            v-model="model.status"
                            :label="
                                $t('resources.notification.attributes.status')
                            "
                            row
                        >
                            <v-radio
                                v-for="option in statusOptions"
                                :key="option.value"
                                :label="option.text"
                                :value="option.value"
                            />
                        </v-radio-group>
                    </v-col>

                    <v-col cols="12">
                        <v-label>{{
                            $t('resources.notification.attributes.description')
                        }}</v-label>

                        <tiptap-vuetify
                            v-model="model.description"
                            :toolbar-attributes="{ color: 'grey lighten-3' }"
                            :extensions="extensions"
                            @init="onEditorCreated"
                        ></tiptap-vuetify>
                        <div class="v-text-field__details">
                            <div class="v-messages theme--light">
                                <div class="v-messages__wrapper">
                                    {{
                                        $t(
                                            'resources.notification.hints.description',
                                        )
                                    }}
                                </div>
                            </div>
                        </div>
                    </v-col>
                    <v-row class="mb-2">
                        <v-col class="grey lighten-4">
                            <label class="subtitle-2">
                                <v-icon left>mdi-account-cog</v-icon>Variáveis
                            </label>

                            <v-chip-group>
                                <span
                                    v-for="(variable, index) in variables"
                                    :key="'var' + index"
                                >
                                    <v-chip
                                        @click="addVariable(variable.name)"
                                        class="ma-2"
                                    >
                                        {{ variable.name }}
                                    </v-chip>
                                </span>
                            </v-chip-group>
                        </v-col>
                    </v-row>
                </v-row>
            </v-card-text>
            <form-actions :loadingSave="loading" />
        </v-card>
    </v-form>
</template>

<script>
import FormActions from '@/shared/components/FormActions'
import CardHeader from '@/shared/components/CardHeader'
import { validationMixin } from 'vuelidate'
import { required, maxLength, between } from 'vuelidate/lib/validators'
import {
    TiptapVuetify,
    Heading,
    Bold,
    Italic,
    Strike,
    Underline,
    Code,
    Paragraph,
    BulletList,
    OrderedList,
    ListItem,
    Link,
    Blockquote,
    HardBreak,
    HorizontalRule,
    History,
} from 'tiptap-vuetify'

export default {
    props: {
        notification: {
            type: Object,
            required: true,
        },
        titlePrefix: String,
    },

    components: {
        FormActions,
        CardHeader,
        TiptapVuetify,
    },

    mixins: [validationMixin],

    data: () => ({
        loading: false,
        typeOptions: [
            { text: 'Clientes', value: 'customers' },
            { text: 'Parceiros', value: 'partners', disabled: true },
        ],
        whenOptions: [
            { text: 'Antes', value: 'before' },
            { text: 'No dia', value: 'on_day' },
            { text: 'Depois', value: 'after' },
        ],
        actionOptions: [{ text: 'Vencimento', value: 'due_date' }],
        variables: [
            { name: '{cliente.nome}' },
            { name: '{cliente.email}' },
            { name: '{cliente.telefone}' },
            { name: '{parceiro.nome}' },
            { name: '{parceiro.email}' },
            { name: '{parceiro.telefone}' },
        ],
        extensions: [
            History,
            Blockquote,
            Link,
            Underline,
            Strike,
            Italic,
            ListItem,
            BulletList,
            OrderedList,
            [
                Heading,
                {
                    options: {
                        levels: [1, 2, 3],
                    },
                },
            ],
            Bold,
            Code,
            HorizontalRule,
            Paragraph,
            HardBreak,
        ],
        editor: null,
    }),

    validations() {
        return {
            model: {
                title: { required, maxLength: maxLength(255) },
                description: { required },
                action: { required },
                type: { required, maxLength: maxLength(50) },
                when: { required, maxLength: maxLength(50) },
                days: { required, between: between(0, 10) },
            },
        }
    },

    computed: {
        model() {
            return this.notification
        },
        cardTitle() {
            return this.$t('forms.title', {
                prefix: this.titlePrefix,
                resource: this.$tc(
                    'resources.notification.name',
                    1,
                ).toLowerCase(),
            })
        },
        statusOptions() {
            return [
                { text: this.$t('form.status.active'), value: true },
                { text: this.$t('form.status.disabled'), value: false },
            ]
        },
        titleErrors() {
            const errors = []
            if (!this.$v.model.title.$dirty) return errors
            !this.$v.model.title.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.notification.attributes.title',
                        ).toLowerCase(),
                    }),
                )
            !this.$v.model.title.maxLength &&
                errors.push(
                    this.$t('forms.errors.maxLength', {
                        label: this.$t(
                            'resources.notification.attributes.title',
                        ).toLowerCase(),
                        length: 255,
                    }),
                )
            return errors
        },
        descriptionErrors() {
            const errors = []
            if (!this.$v.model.description.$dirty) return errors
            !this.$v.model.description.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.notification.attributes.description',
                        ).toLowerCase(),
                    }),
                )
            return errors
        },
        actionErrors() {
            const errors = []
            if (!this.$v.model.action.$dirty) return errors
            !this.$v.model.action.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.notification.attributes.action',
                        ).toLowerCase(),
                    }),
                )
            return errors
        },
        typeErrors() {
            const errors = []
            if (!this.$v.model.type.$dirty) return errors
            !this.$v.model.type.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.notification.attributes.type',
                        ).toLowerCase(),
                    }),
                )
            !this.$v.model.type.maxLength &&
                errors.push(
                    this.$t('forms.errors.maxLength', {
                        label: this.$t(
                            'resources.notification.attributes.type',
                        ).toLowerCase(),
                        length: 50,
                    }),
                )
            return errors
        },
        whenErrors() {
            const errors = []
            if (!this.$v.model.when.$dirty) return errors
            !this.$v.model.when.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.notification.attributes.when',
                        ).toLowerCase(),
                    }),
                )
            !this.$v.model.when.maxLength &&
                errors.push(
                    this.$t('forms.errors.maxLength', {
                        label: this.$t(
                            'resources.notification.attributes.when',
                        ).toLowerCase(),
                        length: 50,
                    }),
                )
            return errors
        },
        daysErrors() {
            const errors = []
            if (!this.$v.model.days.$dirty) return errors
            !this.$v.model.days.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.notification.attributes.days',
                        ).toLowerCase(),
                    }),
                )
            !this.$v.model.days.between &&
                errors.push(
                    this.$t('forms.errors.between', {
                        label: this.$t(
                            'resources.notification.attributes.days',
                        ).toLowerCase(),
                        min: 0,
                        max: 10,
                    }),
                )
            return errors
        },
    },

    methods: {
        addVariable(variable) {
            const transaction = this.editor.state.tr.insertText(variable)
            this.editor.view.dispatch(transaction)
        },

        onEditorCreated({ editor }) {
            this.editor = editor
        },

        async save() {
            this.loading = true
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.loading = false
                return
            }
            let res = await this.model.save()
            this.loading = false
            if (!res) return

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('forms.messages.saved', {
                    resource: this.$tc(
                        'resources.notification.name',
                        1,
                    ).toLowerCase(),
                    name: this.model.title,
                }),
            )

            this.$emit('saved')
        },
    },
}
</script>

<style></style>
